export type ExportDownloadFile = {
  fileName?: string;
  downloadUrl: string;
  sizeBytes?: number;
};

export type ExportJobDownloadSource = {
  jobId?: string;
  fileName?: string;
  downloadUrl?: string;
  downloadFiles?: ExportDownloadFile[];
};

export const getExportDownloadFiles = (job: ExportJobDownloadSource): ExportDownloadFile[] => {
  const fromList = (job.downloadFiles ?? []).filter((file) => Boolean(file.downloadUrl?.trim()));
  if (fromList.length > 0) {
    return fromList;
  }

  if (job.downloadUrl?.trim()) {
    return [{
      downloadUrl: job.downloadUrl,
      fileName: job.fileName,
    }];
  }

  return [];
};

export const getExportDownloadStartMessage = (partCount: number): string => {
  if (partCount > 1) {
    return `Connecting to storage for ${partCount} export parts. Large files may take a moment before they appear in your browser downloads.`;
  }

  return "Connecting to storage. Large files may take a moment before they appear in your browser downloads.";
};

export const EXPORT_DOWNLOAD_HELP_TEXT =
  "Exports are zipped in the cloud first. When you download, your browser pulls the file from storage, so large exports may take a moment to start.";

/**
 * Starts a direct blob download without navigating the app tab.
 * Chrome may still show activity while Azure begins streaming the file.
 */
export const triggerNativeDownload = (url: string) => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = url;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    iframe.remove();
  }, 120000);
};

export const startExportJobDownloads = (files: ExportDownloadFile[]): number => {
  if (files.length === 0) {
    return 0;
  }

  // First part must start synchronously in the click handler.
  triggerNativeDownload(files[0].downloadUrl);

  for (let index = 1; index < files.length; index += 1) {
    window.setTimeout(() => {
      triggerNativeDownload(files[index].downloadUrl);
    }, index * 1000);
  }

  return files.length;
};

export const triggerExportJobDownloads = async (
  job: ExportJobDownloadSource,
  refreshJob?: (jobId: string) => Promise<ExportJobDownloadSource | null>,
): Promise<number> => {
  const cachedFiles = getExportDownloadFiles(job);
  if (cachedFiles.length > 0) {
    return startExportJobDownloads(cachedFiles);
  }

  if (refreshJob && job.jobId) {
    try {
      const fresh = await refreshJob(job.jobId);
      const refreshedFiles = getExportDownloadFiles(fresh ?? job);
      if (refreshedFiles.length > 0) {
        return startExportJobDownloads(refreshedFiles);
      }
    } catch {
      // Fall back to whatever is already on the job row.
    }
  }

  return 0;
};

export const mergeExportJobDownloads = <T extends ExportJobDownloadSource>(
  existing: T,
  incoming: T,
): T => {
  const existingFiles = existing.downloadFiles ?? [];
  const incomingFiles = incoming.downloadFiles ?? [];

  if (incomingFiles.length === 0) {
    return incomingFiles.length === 0 && existingFiles.length > 0
      ? { ...incoming, downloadFiles: existingFiles }
      : incoming;
  }

  if (incomingFiles.length >= existingFiles.length) {
    return incoming;
  }

  const merged = new Map<string, ExportDownloadFile>();
  for (const file of existingFiles) {
    merged.set(file.fileName ?? file.downloadUrl, file);
  }
  for (const file of incomingFiles) {
    merged.set(file.fileName ?? file.downloadUrl, file);
  }

  return {
    ...incoming,
    downloadFiles: Array.from(merged.values()),
  };
};
